
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import PFormCheckBox from "../PFormCheckBox/PFormCheckBox.vue";
import PDragAndDrop from "../PDragAndDrop/PDragAndDrop.vue";
import PButton from "../PButton/PButton.vue";
import { FieldPTable, FieldPTableType, PTableType } from "./pTable.type";
import { iTranslationsPPersonalizeTable, translations } from "./pPersonalizeTable.translations";
import { i18n } from "@/i18n";

interface OrderTuple {
  field: FieldPTable;
  index: number;
}

@Component({
  components: {
    PFormCheckBox,
    PDragAndDrop,
    PButton,
  },
})
export class PPersonalizeTable extends Vue {
  @Prop({ required: true }) value!: PTableType;
  @Watch("value", { deep: true, immediate: true })
  onChangeValue(): void {
  this.fixedOrder = [];
  this.fixedOrderReport = [];
  this.updateLocalValues();
  if (!this.value || !Array.isArray(this.value.fields) || !Array.isArray(this.value.fieldsReport)) {
    return;
  }
  this.value.fields.forEach((fi, idx) => {
    if (fi.fixedOrder) this.fixedOrder.push({ field: fi, index: idx });
  });
  this.value.fieldsReport.forEach((fi, idx) => {
    if (fi.fixedOrder) this.fixedOrderReport.push({ field: fi, index: idx });
  });

  // Keep checkbox lists pretty (this only affects the left panels)
  this.sortValues();

  // 🔒 Build drag lists from the MODEL order (or orderId), not from the checkbox layout
  this.valueLocalSorted = this.buildDragListFromModel(false);
  this.valueLocalSortedReport = this.buildDragListFromModel(true);
}


  @Prop({ required: false }) groups!: string[];
  @Prop({ required: false }) defaultValue!: FieldPTable[];
  @Prop({ required: false }) defaultValueReport!: FieldPTable[];
  @Prop({ required: false, default: "es" }) language!: string;
  @Prop({ required: false }) translation!: iTranslationsPPersonalizeTable;

  fixedOrder: OrderTuple[] = [];
  fixedOrderReport: OrderTuple[] = [];
  valueLocal: FieldPTable[] = [];
  valueLocalSorted: FieldPTable[] = [];
  valueLocalReport: FieldPTable[] = [];
  valueLocalSortedReport: FieldPTable[] = [];
  groupFields: (string | undefined)[] = [];
  groupTitleFields: (string | undefined)[] = [];
  translator: iTranslationsPPersonalizeTable = translations[0].translations;


  // Helper to detect ExtraData group robustly
  private isExtraDataGroup(groupField?: string): boolean {
    if (!groupField) return false;
    const s = groupField.toLowerCase();
    return (
      s.includes('extradata') ||
      s.includes('extrada') ||
      s === '' + i18n.t('transactions.field.groupFieldExtradaData').toLowerCase() ||
      s === 'datos adicionales' // add more variants if needed
    );
  }

  getGroupTitle(isReport = false): (string | undefined)[] {
    if (isReport) {
      const list = this.groupTitleFields.filter(
        v => v != '' + i18n.t('transactions.field.groupFieldTransationData')
      );
      return [...list].sort((a, b) => this.groupRank(a) - this.groupRank(b));
    } else {
      const list = this.groupTitleFields.filter(
        v => v != '' + i18n.t('transactions.field.groupFieldTransationReport')
      );
      return [...list].sort((a, b) => this.groupRank(a) - this.groupRank(b));
    }
  }

  created(): void {
    const language = this.language.split("-").shift();
    const translation = translations.find((tran) => tran.language === language)?.translations;
    if (translation) {
      this.translator = translation;
    }
    if (this.translation) {
      this.translator = Object.assign({}, this.translator, this.translation);
    }
  }


  get selectedReportCount(): number {
    return this.valueLocalReport.filter(f => f.show && !f.fixedOrder).length;
  }

  isHidden(fi: FieldPTable): boolean {
    return fi.type == FieldPTableType.HIDDEN;
  }
  isShowable(fi: FieldPTable): boolean {
    if (this.isHidden(fi)) return false;
    return fi.show && !fi.fixedOrder;
  }
  showTitle(index: number): boolean {
    if (index == 0 || this.groupFields[index] != this.groupFields[index - 1]) {
      return true;
    } else {
      return false;
    }
  }
  resetLocalValues(): void {
    this.valueLocal = [];
    this.valueLocalSorted = [];
    this.valueLocalReport = [];
    this.valueLocalSortedReport = [];
    this.groupFields = [];
  }
  getFieldsByGroup(groupTitle: string): FieldPTable[] {
    return this.valueLocal.filter((field) => field.type != FieldPTableType.HIDDEN && field.groupField == groupTitle);
  }
  getFieldsReportByGroup(groupTitle: string): FieldPTable[] {
    return this.valueLocalReport.filter((field) => field.type != FieldPTableType.HIDDEN && field.groupField == groupTitle);
  }

  private buildDragListFromModel(isReport = false): FieldPTable[] {
  const src: FieldPTable[] = Array.isArray(
    isReport ? this.value?.fieldsReport : this.value?.fields
  )
    ? (isReport ? this.value!.fieldsReport : this.value!.fields)
    : [];

  // Prefer saved orderId if it exists; otherwise keep current array order.
  const ordered = [...src].sort((a, b) => {
    const ao = (a as any).orderId ?? Number.MAX_SAFE_INTEGER;
    const bo = (b as any).orderId ?? Number.MAX_SAFE_INTEGER;
    return ao - bo;
  });

  const out: FieldPTable[] = [];
  ordered.forEach(f => {
    if (f.fixedOrder || f.type === FieldPTableType.HIDDEN) return;
    if (!f.show) return;
    const norm = this.normalizeForDrag({ ...f });
    if (norm) out.push(norm);
  });
  return out;
}


  updateLocalValues(setDefaults?: boolean): void {
    this.resetLocalValues();
    // set to defaults, merge with customer selected
    let values: FieldPTable[] = [];
    let valuesReport: FieldPTable[] = [];
    if (setDefaults) {
      values = Array.isArray(this.defaultValue) ? [...this.defaultValue] : [];
      valuesReport = Array.isArray(this.defaultValueReport) ? [...this.defaultValueReport] : [];
    } else {
      values = Array.isArray(this.value?.fields) ? Object.assign([], this.value.fields) : [];
      valuesReport = Array.isArray(this.value?.fieldsReport) ? Object.assign([], this.value.fieldsReport) : [];
    }

    values.map((fi: FieldPTable, idx: number) => {
      const localField = { ...fi };
      // fill actions label so customer can sort it
      if (this.isShowable(localField) && localField.type == FieldPTableType.ACTION && !localField.label)
        localField.label = this.translator.actions?.toString() || "";
      // values for check table
      this.valueLocal.push({ ...localField });
      // values for order column
      if (this.isShowable(localField)) {
        this.valueLocalSorted.push(localField);
      }
      // a similar array for group titles
      this.groupFields.splice(idx, 0, localField.groupField);
    });
    valuesReport.map((fi: FieldPTable, idx: number) => {
      const localField = { ...fi };
      // fill actions label so customer can sort it
      if (this.isShowable(localField) && localField.type == FieldPTableType.ACTION && !localField.label)
        localField.label = this.translator.actions?.toString() || "";
      // values for check table
      this.valueLocalReport.push({ ...localField });
      // values for order column
      if (this.isShowable(localField)) {
        this.valueLocalSortedReport.push(localField);
      }
      // a similar array for group titles
      this.groupFields.splice(idx, 0, localField.groupField);
    });
    // uniques
    this.groupFields = this.groupFields.filter((v, i, a) => a.indexOf(v) === i);
    this.groupTitleFields = this.groupFields;
  }
  changeCheck(field: FieldPTable): void {
    const fieldOrder = this.valueLocalSorted.find((fi) => fi.key == field.key);
    if (fieldOrder) {
      fieldOrder.show = field.show;
      const idx = this.valueLocalSorted.indexOf(fieldOrder);
      this.valueLocalSorted.splice(idx, 1);
    } else {
      this.valueLocalSorted.push({ ...field });
    }
  }
  changeCheckReport(field: FieldPTable): void {
    const fieldOrder = this.valueLocalSortedReport.find((fi) => fi.key == field.key);
    if (fieldOrder) {
      if (this.valueLocalSortedReport.length <= 1) return;
      fieldOrder.show = field.show;
      const idx = this.valueLocalSortedReport.indexOf(fieldOrder);
      this.valueLocalSortedReport.splice(idx, 1);
    } else {
      this.valueLocalSortedReport.push({ ...field });
    }
  }
  restore(): void {
  // 1) Clear any previous fixed maps (avoid duplicates across restores)
  this.fixedOrder = [];
  this.fixedOrderReport = [];

  // 2) Rebuild ALL local buffers from the current prop `value`
  this.updateLocalValues(false);

  // 3) Re-capture fixed positions from the fresh model
  if (this.value && Array.isArray(this.value.fields)) {
    this.value.fields.forEach((fi, idx) => {
      if (fi.fixedOrder) this.fixedOrder.push({ field: fi, index: idx });
    });
  }
  if (this.value && Array.isArray(this.value.fieldsReport)) {
    this.value.fieldsReport.forEach((fi, idx) => {
      if (fi.fixedOrder) this.fixedOrderReport.push({ field: fi, index: idx });
    });
  }

  // 4) Sort the left lists (so “checked first” view is restored)
  this.sortValues();

  // 5) Force reactivity for PDragAndDrop (in case it watches array identity)
  this.valueLocalSorted = [...this.valueLocalSorted];
  this.valueLocalSortedReport = [...this.valueLocalSortedReport];
}

private normalizeForDrag(f: FieldPTable): FieldPTable | null {
  if (f.type === FieldPTableType.HIDDEN || f.fixedOrder) return null;

  // ensure label for ACTION (was missing when previously not showable)
  if ((!f.label || String(f.label).trim() === "") && f.type === FieldPTableType.ACTION) {
    f = { ...f, label: this.translator.actions?.toString() || "" };
  }

  // still skip truly unlabeled items
  if (!f.label || String(f.label).trim() === "") return null;

  return { ...f, show: true };
}
private buildDragList(isReport = false): FieldPTable[] {
  const titles = this.getGroupTitle(isReport);
  const out: FieldPTable[] = [];
  titles.forEach(title => {
    const perGroup = isReport
      ? this.getFieldsReportByGroup(title!)
      : this.getFieldsByGroup(title!);

    perGroup.forEach(f => {
      const norm = this.normalizeForDrag(f);
      if (norm && f.show) out.push(norm); // keep only visible
    });
  });
  return out;
}


selectAll(): void {
  const markAll = (arr: FieldPTable[]) => {
    arr.forEach(f => {
      if (!f.fixedOrder && f.type !== FieldPTableType.HIDDEN) f.show = true;
    });
  };
  markAll(this.valueLocal);
  markAll(this.valueLocalReport);

  const buildDrag = (isReport = false): FieldPTable[] => {
    const titles = this.getGroupTitle(isReport);
    const out: FieldPTable[] = [];
    titles.forEach(title => {
      const perGroup = isReport
        ? this.getFieldsReportByGroup(title!)
        : this.getFieldsByGroup(title!);

      perGroup.forEach(f => {
        const norm = this.normalizeForDrag(f);
        if (norm) out.push(norm);
      });
    });
    return out;
  };

  this.valueLocalSorted = buildDrag(false);
  this.valueLocalSortedReport = buildDrag(true);

  this.valueLocalSorted = [...this.valueLocalSorted];
  this.valueLocalSortedReport = [...this.valueLocalSortedReport];
}



  restoreDefault(): void {
   this.$emit('reset-defaults');
  }
  sortValues(): void {
    this.valueLocal.sort((a: FieldPTable, b: FieldPTable) => {
      if (a.show) {
        if (b.show) {
          return 0;
        } else {
          return -1
        }
        return 1;
      } else {
        if (b.show) {
          return 1
        } else {
          if ((a.label || "").toLowerCase() < (b.label || "").toLowerCase()) {
            return -1;
          } else {
            return 1;
          }
        }
      };
    });
    this.valueLocalReport.sort((a: FieldPTable, b: FieldPTable) => {
      if (a.show) {
        if (b.show) {
          return 0;
        } else {
          return -1
        }
        return 1;
      } else {
        if (b.show) {
          return 1
        } else {
          if ((a.label || "").toLowerCase() < (b.label || "").toLowerCase()) {
            return -1;
          } else {
            return 1;
          }
        }
      };
    });
  }
// Helper: asigna orderId según sortedList y coloca el resto al final.
// No asigna orderId a fixedOrder ni a items con show === false.
private assignOrderIdsBySorted(
  allFields: FieldPTable[],
  sortedList: FieldPTable[]
): void {
  const sortedKeys = new Set(sortedList.map(f => f.key));
  const findByKey = (k: string) => allFields.find(x => x.key === k);

  // Limpia orderId de todo lo que no debe consumir número
  allFields.forEach(f => {
    if (f.fixedOrder || !f.show) {
      (f as any).orderId = undefined;
    }
  });

  let next = 1;

  // 1) Primero, los del drag-and-drop (en orden)
  for (const f of sortedList) {
    if (f.fixedOrder || !f.show) continue;
    const target = findByKey(f.key);
    if (target) (target as any).orderId = next++;
  }

  // 2) Luego, los visibles y no fixed que NO están en sorted (van al final)
  for (const f of allFields) {
    if (f.fixedOrder || !f.show) continue;
    if (!sortedKeys.has(f.key)) {
      (f as any).orderId = next++;
    }
  }
}

  private groupRank(g?: string): number {
    const s = (g || '').toLowerCase();
    const isReport =
      s.includes('datos del reporte') || s.includes('datos de reporte') ||
      s.includes('datos transacción') || s.includes('transacción') || s.includes('reporte');
    const isFechaValor = s.includes('fecha valor') || s.includes('datos fecha valor');
    const isAdicionales = s.includes('adicional') || s.includes('datos adicionales');
    if (isReport) return 0;
    if (isFechaValor) return 1;
    if (isAdicionales) return 2;
    return Number.MAX_SAFE_INTEGER;
  }


apply(): void {
  let valueTemp: FieldPTable[] = [];
  let valueTempReport: FieldPTable[] = [];
  let sortedIdx = 0;

  // --- GRID (fields) ---
  // Rebuild non-fixed items: if showable => take from valueLocalSorted (drag order); else keep as-is (hidden stays hidden)
  this.valueLocal
    .filter((fi) => !fi.fixedOrder)
    .forEach((fi) => {
      if (this.isShowable(fi)) {
        // take next in drag order
        valueTemp.push({ ...this.valueLocalSorted[sortedIdx] });
        sortedIdx++;
      } else {
        // keep hidden/non-showable item to preserve its state
        valueTemp.push({ ...fi });
      }
    });

  // Reinsert fixed items at their original index
  this.fixedOrder.forEach((fi) => {
    valueTemp.splice(fi.index, 0, fi.field);
  });

  // --- REPORT (fieldsReport) ---
  sortedIdx = 0;
  this.valueLocalReport
    .filter((fi) => !fi.fixedOrder)
    .forEach((fi) => {
      if (this.isShowable(fi)) {
        valueTempReport.push({ ...this.valueLocalSortedReport[sortedIdx] });
        sortedIdx++;
      } else {
        valueTempReport.push({ ...fi });
      }
    });

  this.fixedOrderReport.forEach((fi) => {
    valueTempReport.splice(fi.index, 0, fi.field);
  });

  const assignOrderIds = (arr: FieldPTable[]) => {
    let n = 1;
    arr.forEach(f => {
      if (!f.fixedOrder && f.show) (f as any).orderId = n++;
      else (f as any).orderId = undefined;
    });
  };
  assignOrderIds(valueTemp);
  assignOrderIds(valueTempReport);


  const valueReturn = { ...this.value };
  valueReturn.fields = valueTemp;
  valueReturn.fieldsReport = valueTempReport;

  this.$emit("input", valueReturn);
  this.$emit("change", valueReturn);
}


}
export default PPersonalizeTable;
